import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '../api/client.js';
import clsx from 'clsx';
import OpportunityEditor from '../components/OpportunityEditor.jsx';

const TABS = [
  { id: 'revenue',  label: 'Revenue' },
  { id: 'fadeout',  label: 'Fade-out customers' },
  { id: 'crosssell', label: 'Cross-sell candidates' },
  { id: 'comeback', label: 'Comeback rate' },
  { id: 'warranty', label: 'Warranty' },
  { id: 'pipeline', label: 'Pipeline' },
];

function fmt$(v) {
  const n = Number(v || 0);
  if (n === 0) return '$0';
  if (n >= 1_000_000) return '$' + (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1000) return '$' + Math.round(n).toLocaleString();
  return '$' + n.toFixed(0);
}
function fmtMonth(year, month) {
  return `${year}-${String(month).padStart(2,'0')}`;
}

export default function Service() {
  const [tab, setTab] = useState('revenue');
  const [leadDraft, setLeadDraft] = useState(null);

  return (
    <div className="space-y-4">
      <header>
        <div className="section-eyebrow">Service</div>
        <h1 className="text-2xl">Service</h1>
        <div className="rule-crimson" />
      </header>

      <div className="border-b border-panelDark flex gap-1 flex-wrap">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={clsx(
              'px-4 py-2 text-sm border-b-2 -mb-px',
              tab === t.id ? 'border-crimson text-crimson font-medium' : 'border-transparent text-navy hover:text-crimson'
            )}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'revenue'   && <RevenueTab onCreateLead={setLeadDraft} />}
      {tab === 'fadeout'   && <FadeOutTab onCreateLead={setLeadDraft} />}
      {tab === 'crosssell' && <CrossSellTab />}
      {tab === 'comeback'  && <ComebackTab />}
      {tab === 'warranty'  && <WarrantyTab />}
      {tab === 'pipeline'  && <PipelineTab />}

      {leadDraft && (
        <OpportunityEditor
          initial={leadDraft}
          onClose={() => setLeadDraft(null)}
          onSaved={() => setLeadDraft(null)}
        />
      )}
    </div>
  );
}

function RevenueTab({ onCreateLead }) {
  const q = useQuery({
    queryKey: ['service', 'revenue'],
    queryFn: () => api.get('/service/revenue').then(r => r.data),
  });
  if (q.isLoading) return <div className="text-muted">Loading…</div>;
  if (q.isError)   return <div className="text-crimson">Failed to load.</div>;

  const trend = q.data?.trend || [];
  const top = q.data?.top_customers || [];
  const branches = q.data?.by_branch || [];

  const last12  = trend.slice(-12).reduce((s, r) => s + Number(r.service_sales || 0), 0);
  const prior12 = trend.slice(-24, -12).reduce((s, r) => s + Number(r.service_sales || 0), 0);
  const yoy = prior12 > 0 ? (last12 - prior12) / prior12 : null;
  const maxMonth = trend.reduce((m, r) => Math.max(m, Number(r.service_sales || 0)), 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <Stat label="Last 12 months" value={fmt$(last12)} />
        <Stat label="Prior 12 months" value={fmt$(prior12)} />
        <Stat label="YoY"
              value={yoy == null ? '—' : `${yoy >= 0 ? '+' : ''}${(yoy * 100).toFixed(1)}%`}
              highlight={yoy != null && yoy < 0} />
      </div>
      <p className="text-xs text-muted">
        Revenue spans Karmak history (2014–Sep 2025) and Excede going forward.
      </p>

      <section>
        <h2 className="text-sm font-semibold text-navy uppercase tracking-wide mb-2">Monthly trend (last 24 months)</h2>
        <div className="bg-white border border-panelDark rounded p-3 space-y-1">
          {trend.map(r => (
            <div key={`${r.year}-${r.month}`} className="flex items-center gap-2 text-xs">
              <span className="font-mono text-muted w-16">{fmtMonth(r.year, r.month)}</span>
              <div className="flex-1 bg-panel rounded h-4 relative overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-navy/80 rounded"
                     style={{ width: `${maxMonth ? (Number(r.service_sales) / maxMonth) * 100 : 0}%` }} />
              </div>
              <span className="w-24 text-right tabular-nums">{fmt$(r.service_sales)}</span>
              <span className="w-12 text-right text-muted text-[11px]">{r.service_invoices} inv</span>
            </div>
          ))}
          {trend.length === 0 && <div className="text-muted text-sm">No data.</div>}
        </div>
      </section>

      <div className="grid md:grid-cols-2 gap-4">
        <section>
          <h2 className="text-sm font-semibold text-navy uppercase tracking-wide mb-2">By branch (12mo)</h2>
          <div className="bg-white border border-panelDark rounded">
            <table className="w-full text-sm">
              <tbody>
                {branches.map(b => (
                  <tr key={b.branch_id} className="border-t border-panel first:border-t-0">
                    <td className="px-3 py-1.5">{b.branch_name}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{fmt$(b.service_12mo)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <section>
          <h2 className="text-sm font-semibold text-navy uppercase tracking-wide mb-2">Top customers (12mo)</h2>
          <div className="bg-white border border-panelDark rounded">
            <table className="w-full text-sm">
              <tbody>
                {top.slice(0, 15).map(c => (
                  <tr key={c.customer_id} className="border-t border-panel first:border-t-0">
                    <td className="px-3 py-1.5 truncate">
                      <Link to={`/customers/${c.customer_id}`} className="text-navy hover:text-crimson">{c.name}</Link>
                      {Number(c.row_count) > 1 && (
                        <span className="ml-2 text-[10px] text-muted">({c.row_count} rows merged)</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{fmt$(c.service_12mo)}</td>
                    <td className="px-3 py-1.5 text-right">
                      <button
                        onClick={() => onCreateLead({
                          name: `${c.name} — service lead`,
                          customer_id: c.customer_id,
                          lead_source: 'Service referral',
                          domain: 'trailer_sales',
                        })}
                        className="text-xs text-crimson hover:underline whitespace-nowrap">
                        + Lead
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}

function FadeOutTab({ onCreateLead }) {
  const [silence, setSilence]   = useState(6);
  const [baseline, setBaseline] = useState(18); // 6mo silence + 18mo baseline = active in last 2yr, quiet last 6mo
  const [minAvg, setMinAvg]     = useState(200);
  const [branchId, setBranchId] = useState('');

  const q = useQuery({
    queryKey: ['service', 'fade-out', silence, baseline, minAvg, branchId],
    queryFn: () => {
      const p = new URLSearchParams();
      p.set('silence_threshold_months', silence);
      p.set('baseline_months', baseline);
      p.set('min_baseline_avg', minAvg);
      p.set('limit', '100');
      if (branchId) p.set('branch_id', branchId);
      return api.get(`/service/fade-out?${p.toString()}`).then(r => r.data);
    },
    keepPreviousData: true,
  });
  const totalAtRisk = (q.data?.data || []).reduce((s, r) => s + Number(r.at_risk_score || 0), 0);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted">
        Customers who used to bring trailers in for service and have gone quiet. Score = baseline monthly average × months silent.
      </p>

      <div className="bg-panel rounded p-2 flex flex-wrap items-center gap-3 text-sm">
        <Field label="Silence threshold" suffix="mo">
          <input type="number" min="1" max="36" value={silence}
            onChange={e => setSilence(parseInt(e.target.value, 10) || 6)}
            className="w-16 px-2 py-1 rounded border border-panelDark bg-white" />
        </Field>
        <Field label="Baseline window" suffix="mo">
          <input type="number" min="3" max="36" value={baseline}
            onChange={e => setBaseline(parseInt(e.target.value, 10) || 12)}
            className="w-16 px-2 py-1 rounded border border-panelDark bg-white" />
        </Field>
        <Field label="Min baseline" suffix="$/mo">
          <input type="number" min="0" max="100000" step="50" value={minAvg}
            onChange={e => setMinAvg(parseFloat(e.target.value) || 0)}
            className="w-20 px-2 py-1 rounded border border-panelDark bg-white" />
        </Field>
        <select value={branchId} onChange={e => setBranchId(e.target.value)}
                className="px-2 py-1 rounded border border-panelDark bg-white">
          <option value="">All branches</option>
          <option value="3">Fontana</option>
          <option value="2">Fresno</option>
          <option value="1">Lathrop</option>
          <option value="4">Delhi</option>
          <option value="5">Troutdale</option>
        </select>
        <div className="ml-auto text-xs text-muted">
          {q.isLoading ? 'Loading…' : `${(q.data?.data || []).length} rows · ${fmt$(totalAtRisk)} at risk`}
        </div>
      </div>

      <div className="bg-white border border-panelDark rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-panel text-navy uppercase text-[10px] tracking-wide">
            <tr>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Branch</th>
              <th className="text-right px-3 py-2">Baseline avg</th>
              <th className="text-right px-3 py-2">Silent</th>
              <th className="text-right px-3 py-2">Last service</th>
              <th className="text-right px-3 py-2">At-risk score</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {(q.data?.data || []).map(r => (
              <tr key={r.customer_id} className="border-t border-panel hover:bg-panel/60">
                <td className="px-3 py-1.5">
                  <Link to={`/customers/${r.customer_id}`} className="text-navy hover:text-crimson">{r.name}</Link>
                </td>
                <td className="px-3 py-1.5 text-xs text-muted">{r.branch_name || '—'}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmt$(r.baseline_avg)}/mo</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{r.months_silent}m</td>
                <td className="px-3 py-1.5 text-right text-xs text-muted">{r.last_service_at?.slice(0,7) || '—'}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-medium">{fmt$(r.at_risk_score)}</td>
                <td className="px-3 py-1.5 text-right">
                  <button
                    onClick={() => onCreateLead({
                      name: `${r.name} — re-engage lead`,
                      customer_id: r.customer_id,
                      lead_source: 'Service referral',
                      domain: 'trailer_sales',
                    })}
                    className="text-xs text-crimson hover:underline whitespace-nowrap">
                    + Lead
                  </button>
                </td>
              </tr>
            ))}
            {!q.isLoading && (q.data?.data || []).length === 0 && (
              <tr><td colSpan={6} className="text-center text-muted py-6">No fade-out customers match these thresholds.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ComebackTab() {
  const [windowDays, setWindowDays] = useState(180);
  const q = useQuery({
    queryKey: ['service', 'comeback', windowDays],
    queryFn: () => api.get(`/service/comeback-rate?window_days=${windowDays}`).then(r => r.data),
  });
  const byBranch = q.data?.by_branch || [];
  const list = q.data?.comebacks || [];
  const total = byBranch.reduce((s, r) => s + Number(r.total_invoiced), 0);
  const cb    = byBranch.reduce((s, r) => s + Number(r.comebacks), 0);
  const pct   = total > 0 ? (100 * cb / total).toFixed(2) : '—';

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted">
        Comeback flag (set on the RO header by the writer) means the customer brought the trailer back for re-work.
        Limited to ~7 months of Excede data.
      </p>

      <div className="flex items-center gap-3 text-sm">
        <Field label="Window" suffix="days">
          <input type="number" min="7" max="730" step="30" value={windowDays}
            onChange={e => setWindowDays(parseInt(e.target.value, 10) || 90)}
            className="w-20 px-2 py-1 rounded border border-panelDark bg-white" />
        </Field>
        <div className="ml-auto">
          <Stat label={`Overall · last ${windowDays}d`} value={`${pct}%`} small />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {byBranch.map(b => (
          <Stat key={b.external_branch_id}
                label={`Branch ${b.external_branch_id}`}
                value={`${b.comeback_pct}%`}
                sub={`${b.comebacks} / ${b.total_invoiced}`}
                small />
        ))}
      </div>

      <section>
        <h2 className="text-sm font-semibold text-navy uppercase tracking-wide mb-2">Recent comebacks</h2>
        <div className="bg-white border border-panelDark rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-panel text-navy uppercase text-[10px] tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">RO#</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Trailer</th>
                <th className="text-right px-3 py-2">Br</th>
                <th className="text-right px-3 py-2">Invoiced</th>
                <th className="text-right px-3 py-2">$ Subtotal</th>
                <th className="text-left px-3 py-2">Writer / Tech</th>
              </tr>
            </thead>
            <tbody>
              {list.map(r => (
                <tr key={r.id} className="border-t border-panel">
                  <td className="px-3 py-1.5 font-mono text-xs">{r.sls_id}</td>
                  <td className="px-3 py-1.5">{r.cus_name}</td>
                  <td className="px-3 py-1.5 text-xs text-muted">{r.fleet_unt_id || '—'} {r.vin}</td>
                  <td className="px-3 py-1.5 text-right text-xs">{r.external_branch_id}</td>
                  <td className="px-3 py-1.5 text-right text-xs text-muted">{r.date_invoice?.slice(0,10)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmt$(r.amt_subtotal)}</td>
                  <td className="px-3 py-1.5 text-xs text-muted">{r.emp_id || '—'} / {r.emp_id_spn || '—'}</td>
                </tr>
              ))}
              {!q.isLoading && list.length === 0 && (
                <tr><td colSpan={7} className="text-center text-muted py-6">No comebacks in this window.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function CrossSellTab() {
  const qc = useQueryClient();
  const [requireUnits, setRequireUnits] = useState(true);
  const q = useQuery({
    queryKey: ['cross-sell', 'service_strong_parts_weak', requireUnits],
    queryFn: () => api.get(`/cross-sell?direction=service_strong_parts_weak&limit=100&require_units=${requireUnits}`).then(r => r.data),
  });
  const hide = useMutation({
    mutationFn: (customerId) => api.patch(`/cross-sell/${customerId}/hide`, { hidden: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cross-sell'] }),
  });
  const markTraveling = useMutation({
    mutationFn: (customerId) => api.patch(`/cross-sell/${customerId}/traveling-mechanic`, { traveling: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cross-sell'] }),
  });
  const rows = q.data?.data || [];
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted">
        Customers bringing trailers in for service but not buying parts from us. Pitch parts.
      </p>
      <div className="flex items-center gap-4">
        <div className="text-xs text-muted">{q.isLoading ? 'Loading…' : `${rows.length} candidates`}</div>
        <label className="flex items-center gap-1.5 text-xs text-navy cursor-pointer">
          <input type="checkbox" checked={requireUnits} onChange={e => setRequireUnits(e.target.checked)}
                 className="rounded" />
          Only customers with vehicles in fleet
        </label>
      </div>
      <div className="bg-white border border-panelDark rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-panel text-navy uppercase text-[10px] tracking-wide">
            <tr>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Branch</th>
              <th className="text-right px-3 py-2">Service (12mo)</th>
              <th className="text-right px-3 py-2">Parts (12mo)</th>
              <th className="text-right px-3 py-2">Last parts buy</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.customer_id} className="border-t border-panel hover:bg-panel/60">
                <td className="px-3 py-1.5">
                  <Link to={`/customers/${r.customer_id}`} className="text-navy hover:text-crimson">{r.name}</Link>
                </td>
                <td className="px-3 py-1.5 text-xs text-muted">{r.branch_name || '—'}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmt$(r.strong_12mo)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmt$(r.weak_12mo)}</td>
                <td className="px-3 py-1.5 text-right text-xs text-muted">{r.last_weak_period?.slice(0,7) || '—'}</td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap space-x-3">
                  <button
                    onClick={() => markTraveling.mutate(r.customer_id)}
                    disabled={markTraveling.isPending}
                    title="This customer brings in other people's trailers — exclude always"
                    className="text-xs text-muted hover:underline disabled:opacity-50"
                  >
                    Traveling mechanic
                  </button>
                  <button
                    onClick={() => hide.mutate(r.customer_id)}
                    disabled={hide.isPending}
                    title="Hide this customer from cross-sell lists"
                    className="text-xs text-crimson hover:underline disabled:opacity-50"
                  >
                    Hide
                  </button>
                </td>
              </tr>
            ))}
            {!q.isLoading && rows.length === 0 && (
              <tr><td colSpan={6} className="text-center text-muted py-6">No cross-sell candidates.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PipelineTab() {
  const q = useQuery({
    queryKey: ['service', 'pipeline'],
    queryFn: () => api.get('/service/pipeline').then(r => r.data),
  });
  const rows = q.data?.data || [];

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted">
        Open opportunities tagged Service. Set an opportunity's <em>domain</em> to <code>service</code> to appear here.
      </p>
      <div className="bg-white border border-panelDark rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-panel text-navy uppercase text-[10px] tracking-wide">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Stage</th>
              <th className="text-right px-3 py-2">Value</th>
              <th className="text-right px-3 py-2">Activity</th>
              <th className="text-right px-3 py-2">Close by</th>
              <th className="text-left px-3 py-2">Owner</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(o => (
              <tr key={o.id} className="border-t border-panel hover:bg-panel/60">
                <td className="px-3 py-1.5">
                  <Link to={`/pipeline/${o.id}`} className="text-navy hover:text-crimson font-medium">{o.name}</Link>
                </td>
                <td className="px-3 py-1.5">
                  {o.customer_id
                    ? <Link to={`/customers/${o.customer_id}`} className="text-navy hover:text-crimson">{o.customer_name}</Link>
                    : '—'}
                </td>
                <td className="px-3 py-1.5 capitalize">{o.stage}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmt$(o.value_usd)}</td>
                <td className="px-3 py-1.5 text-right text-xs text-muted">
                  {o.source_note_date ? new Date(o.source_note_date).toLocaleDateString() : '—'}
                </td>
                <td className="px-3 py-1.5 text-right text-xs text-muted">{o.expected_close_date || '—'}</td>
                <td className="px-3 py-1.5 text-xs text-muted">{o.owner_name || '—'}</td>
              </tr>
            ))}
            {!q.isLoading && rows.length === 0 && (
              <tr><td colSpan={7} className="text-center text-muted py-6">No service opportunities yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const AGING_DAYS = 7;
const money = (v) => v == null ? '—' : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

function WarrantyTab() {
  const qc = useQueryClient();
  const [showClosed, setShowClosed] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ description: '', vin: '', claim_amount: '', opened_date: '', notes: '' });
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const q = useQuery({
    queryKey: ['warranty-jobs', showClosed],
    queryFn: () => {
      const params = showClosed ? '' : '?status=open';
      return api.get(`/warranty-jobs${params}`).then(r => r.data.data);
    },
  });

  const close = useMutation({
    mutationFn: ({ id, status }) => api.put(`/warranty-jobs/${id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['warranty-jobs'] }),
  });

  const add = useMutation({
    mutationFn: () => api.post('/warranty-jobs', {
      description: form.description,
      vin: form.vin || null,
      claim_amount: form.claim_amount ? Number(form.claim_amount) : null,
      opened_date: form.opened_date || undefined,
      notes: form.notes || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warranty-jobs'] });
      setAdding(false);
      setForm({ description: '', vin: '', claim_amount: '', opened_date: '', notes: '' });
    },
  });

  const rows = q.data || [];
  const openCount = rows.filter(r => r.status === 'open').length;
  const agingCount = rows.filter(r => r.status === 'open' && (r.days_open || 0) >= AGING_DAYS).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-4 text-sm">
          <span className="text-muted">{openCount} open</span>
          {agingCount > 0 && (
            <span className="text-crimson font-medium">{agingCount} aging ≥{AGING_DAYS}d</span>
          )}
        </div>
        <div className="flex gap-2 items-center">
          <label className="flex items-center gap-1 text-xs text-muted cursor-pointer">
            <input type="checkbox" checked={showClosed} onChange={e => setShowClosed(e.target.checked)} />
            Show closed
          </label>
          <button className="btn-primary text-sm" onClick={() => setAdding(true)}>+ Add job</button>
        </div>
      </div>

      {adding && (
        <div className="card p-4 space-y-3 border border-navy-100">
          <h3 className="font-semibold text-navy">New warranty job</h3>
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <label className="label">Description *</label>
              <input className="input" value={form.description} autoFocus
                     onChange={e => setF('description', e.target.value)} placeholder="e.g. ABS module replacement — warranty claim" />
            </div>
            <div>
              <label className="label">VIN</label>
              <input className="input" value={form.vin} onChange={e => setF('vin', e.target.value)} />
            </div>
            <div>
              <label className="label">Claim amount</label>
              <input className="input" type="number" step="0.01" value={form.claim_amount}
                     onChange={e => setF('claim_amount', e.target.value)} placeholder="$0.00" />
            </div>
            <div>
              <label className="label">Opened date</label>
              <input className="input" type="date" value={form.opened_date}
                     onChange={e => setF('opened_date', e.target.value)} />
            </div>
            <div className="col-span-2">
              <label className="label">Notes</label>
              <input className="input" value={form.notes} onChange={e => setF('notes', e.target.value)} />
            </div>
          </div>
          {add.isError && <div className="text-crimson text-sm">Save failed.</div>}
          <div className="flex gap-2 justify-end">
            <button className="btn-ghost" onClick={() => setAdding(false)}>Cancel</button>
            <button className="btn-primary" disabled={!form.description.trim() || add.isPending}
                    onClick={() => add.mutate()}>
              {add.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-panelDark rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-panel text-navy uppercase text-[10px] tracking-wide">
            <tr>
              <th className="text-left px-3 py-2">Description</th>
              <th className="text-left px-3 py-2">VIN</th>
              <th className="text-left px-3 py-2">Branch</th>
              <th className="text-right px-3 py-2">Opened</th>
              <th className="text-right px-3 py-2">Days open</th>
              <th className="text-right px-3 py-2">Claim</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const aging = r.status === 'open' && (r.days_open || 0) >= AGING_DAYS;
              return (
                <tr key={r.id} className={`border-t border-panel hover:bg-panel/60 ${aging ? 'bg-rose-50/30' : ''}`}>
                  <td className="px-3 py-1.5 max-w-[240px]">
                    <div className="truncate">{r.description}</div>
                    {r.notes && <div className="text-[11px] text-muted truncate">{r.notes}</div>}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs text-muted">{r.vin || '—'}</td>
                  <td className="px-3 py-1.5 text-xs text-muted">{r.branch_name || '—'}</td>
                  <td className="px-3 py-1.5 text-right text-xs text-muted">{r.opened_date}</td>
                  <td className="px-3 py-1.5 text-right">
                    <span className={aging ? 'text-crimson font-semibold' : 'text-muted text-xs'}>
                      {r.days_open ?? 0}d{aging && ' ⚠'}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{money(r.claim_amount)}</td>
                  <td className="px-3 py-1.5">
                    <span className={clsx('text-[11px] px-2 py-0.5 rounded',
                      r.status === 'open'   ? 'bg-amber-100 text-amber-800' :
                      r.status === 'paid'   ? 'bg-green-100 text-green-800' :
                                              'bg-panel text-muted')}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap space-x-2">
                    {r.status === 'open' && (
                      <>
                        <button onClick={() => close.mutate({ id: r.id, status: 'paid' })}
                                disabled={close.isPending}
                                className="text-xs text-green-700 hover:underline disabled:opacity-50">
                          Mark paid
                        </button>
                        <button onClick={() => close.mutate({ id: r.id, status: 'denied' })}
                                disabled={close.isPending}
                                className="text-xs text-muted hover:underline disabled:opacity-50">
                          Denied
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {!q.isLoading && rows.length === 0 && (
              <tr><td colSpan={8} className="text-center text-muted py-6">
                {showClosed ? 'No warranty jobs.' : 'No open warranty jobs.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, highlight, small }) {
  return (
    <div className={clsx(
      'border rounded px-3 py-2',
      small ? 'py-1.5' : '',
      highlight ? 'bg-rose-50 border-rose-200' : 'bg-panel border-panelDark'
    )}>
      <div className="text-xs text-muted uppercase tracking-wide">{label}</div>
      <div className={clsx(small ? 'text-lg' : 'text-2xl', 'font-semibold tabular-nums')}>{value}</div>
      {sub && <div className="text-[11px] text-muted">{sub}</div>}
    </div>
  );
}
function Field({ label, suffix, children }) {
  return (
    <label className="flex items-center gap-1 text-xs text-navy">
      {label} {children}
      {suffix && <span className="text-muted">{suffix}</span>}
    </label>
  );
}
